import React, {Component} from 'react';
import BeatLoader from 'react-spinners/BeatLoader';
import './styles.css';
import {isLoggedOut} from "../../helpers/accountHelper";
import {withTranslation} from "react-i18next";
import {connect} from "react-redux";
import {api, notificationTypes, paneContentModes, slateTypes} from "../../config/constants";
import iframeMessageTypes from "../../config/iframeMessageTypes";
import {showNotification} from "../../store/actions/notification";
import {ColorExtractor} from 'react-color-extractor'
import {updatePage} from "../../helpers/versionsHelper";
import ZoomPanPinch from "../Shared/ZoomPanPinch";
import {SlateDialog} from "../Shared/SlateDialog";
import {
  isPrintPdfMessage,
  printsInNewTab,
  isPrintCaptureOn,
  createPdfUrl,
  printInFrame,
  openPdfTab,
  downloadPdf,
  revokePdfUrl,
} from "../../helpers/pdfPrint";

// Pane fallback: how long a template NAVIGATE may go unconfirmed (no contentLoaded) before the iframe is reloaded.
export var CONTENT_FALLBACK_MS = 8000;
// After a saveTimeout rejection: the longest wait for the pending save, and the backoff before the NAVIGATE is resent.
export var SAVE_WAIT_MS = 10000;
export var SAVE_RESEND_MS = 2000;

export class SlateIframe extends Component {
  constructor(props) {
    super(props);
    this.state = {
      imageUrl: props.imageUrl,
      fullScreenImageUuid: null,
      colors: [],
      iframeKey: 0,
      printDialog: null,
    };
    this.contentTimer = null;
    this.saveWaitTimer = null;
    this.saveResendTimer = null;
    this.contentReloaded = false;
    this.flutterReady = false;
    this.loadedSlateUUID = null;
    this.currentContentMode = null;
    this.iframeRef = React.createRef();
    // The print the pane's Flutter app asked for: {url, kind, filename, frameJob}, or null.
    this.printJob = null;
  }

  sendMessageToFlutter = (type, payload) => {
    var frame = this.iframeRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(
      Object.assign({ type: type }, payload),
      '*'
    );
  }

  buildContentPayload = () => {
    if (this.props.contentJson) {
      return {
        mode: this.props.contentMode || paneContentModes.TEMPLATE,
        slateUuid: null,
        slateType: null,
        imageUrl: null,
        isDisableEditingAllField: false,
        slateData: this.props.contentJson,
      };
    }
    if (this.props.slateUUID) {
      return {
        mode: paneContentModes.SLATE,
        slateUuid: this.props.slateUUID,
        slateType: this.props.slateType || slateTypes.slate,
        imageUrl: this.props.imageUrl || null,
        isDisableEditingAllField: !!this.props.isDisableEditingAllField,
        slateData: null,
      };
    }
    return null;
  }

  buildUserPayload = (user) => {
    user = user || this.props.currentUser;
    if (isLoggedOut(user)) return null;
    var contactInfo = user.contactInfo || {};
    return {
      uuid: user.uuid,
      name: user.name || '',
      displayName: user.displayName,
      personalAccountUUID: user.personalAccountUUID,
      roles: user.roles || [],
      contactInfo: {
        email: contactInfo.email || '',
        phone: contactInfo.phone || '',
        isPhoneVerified: contactInfo.isPhoneVerified || false,
        isEmailVerified: contactInfo.isEmailVerified || false,
      },
    };
  }

  buildStacksPayload = () => {
    var accountsList = this.props.accountsList;
    if (!accountsList) return null;
    return accountsList.map(function (account) {
      return {
        uuid: account.uuid,
        name: account.name,
        imageUrl: account.imageUUID ? (api.GET_IMAGE + '/' + account.imageUUID) : null,
      };
    });
  }

  sendInitMessage = () => {
    var content = this.buildContentPayload();
    this.currentContentMode = content ? content.mode : null;
    this.sendMessageToFlutter(iframeMessageTypes.INIT, {
      user: this.buildUserPayload(),
      stacks: this.buildStacksPayload(),
      currentStackUuid: this.props.currentAccount ? this.props.currentAccount.uuid : null,
      content: content,
      capabilities: { printPdf: true },
    });
  }

  postNavigate = () => {
    var content = this.buildContentPayload();
    if (!content) return null;
    var payload = { content: content };
    if (content.mode === paneContentModes.TEMPLATE || content.mode === paneContentModes.DEMO) {
      payload.force = true;
    }
    console.log('[SlateIframe] sendNavigateMessage', { mode: content.mode, slateUuid: content.slateUuid, force: !!payload.force, payload: payload });
    this.sendMessageToFlutter(iframeMessageTypes.NAVIGATE, payload);
    return content;
  }

  sendNavigateMessage = () => {
    var content = this.postNavigate();
    if (!content) return;
    if (content.mode === paneContentModes.TEMPLATE || content.mode === paneContentModes.DEMO) {
      this.clearSaveWait();
      this.armContentWatchdog();
    }
  }

  // Template and demo content are confirmed by contentLoaded. When Flutter does not confirm in time, the iframe is
  // reloaded once and the template arrives through INIT, the path a browser refresh takes.
  armContentWatchdog = () => {
    this.clearContentTimer();
    this.contentTimer = setTimeout(() => {
      this.contentTimer = null;
      if (this.props.contentJson) {
        this.reloadForContent('timeout');
      }
    }, CONTENT_FALLBACK_MS);
  }

  clearTimer = (name) => {
    if (this[name] !== null) {
      clearTimeout(this[name]);
      this[name] = null;
    }
  }

  clearContentTimer = () => {
    this.clearTimer('contentTimer');
  }

  clearSaveWait = () => {
    this.clearTimer('saveWaitTimer');
    this.clearSaveResend();
  }

  clearSaveResend = () => {
    this.clearTimer('saveResendTimer');
  }

  clearContentTimers = () => {
    this.clearContentTimer();
    this.clearSaveWait();
  }

  isContentPending = () => {
    return this.contentTimer !== null || this.saveWaitTimer !== null || this.saveResendTimer !== null;
  }

  // Flutter rejected the NAVIGATE because a save of the current pane content has not settled. Reloading now would
  // discard that save, so wait for it (contentSaved, else a short backoff), resend, and reload only if the template
  // is still not shown SAVE_WAIT_MS after the first rejection.
  waitForSave = () => {
    this.clearContentTimer();
    if (this.saveWaitTimer === null) {
      this.saveWaitTimer = setTimeout(() => {
        this.saveWaitTimer = null;
        if (this.props.contentJson) {
          this.reloadForContent('saveTimeout');
        }
      }, SAVE_WAIT_MS);
    }
    this.clearSaveResend();
    this.saveResendTimer = setTimeout(() => {
      this.saveResendTimer = null;
      this.postNavigate();
    }, SAVE_RESEND_MS);
  }

  reloadForContent = (reason) => {
    this.clearContentTimers();
    if (this.contentReloaded) {
      console.log('[SlateIframe] content fallback skipped', { reason: reason });
      return;
    }
    this.contentReloaded = true;
    console.warn('[SlateIframe] content fallback reload', { reason: reason });
    this.flutterReady = false;
    this.loadedSlateUUID = null;
    this.currentContentMode = null;
    this.setState({ iframeKey: this.state.iframeKey + 1 });
  }

  sendResetMessage = () => {
    this.sendMessageToFlutter(iframeMessageTypes.RESET);
  }

  handleFlutterReady = () => {
    console.log('[SlateIframe] flutterReady received');
    this.flutterReady = true;
    this.sendInitMessage();
  }

  handler = async (ev) => {
    // Several SlateIframe instances can be mounted at once -- SlatePaneManager
    // always renders SlatePane, even on routes that only hide it with CSS, so
    // the slate page has both its own iframe and the pane's. Each instance
    // listens on window, so without this guard every instance reacts to every
    // message and duplicates its effects (two full screen image overlays, two
    // clipboard notifications, and so on).
    var ownFrame = this.iframeRef.current;
    if (!ownFrame || ev.source !== ownFrame.contentWindow) {
      return;
    }

    // Legacy bare string 'flutterReady' from standalone Flutter
    if (ev.data === 'flutterReady') {
      this.handleFlutterReady();
      return;
    }

    // Guard: ignore messages without proper data structure
    if (!ev.data || typeof ev.data.type !== 'string') {
      return;
    }

    if (ev.data.type === 'bootStage') {
      console.log('[SlateIframe] boot', ev.data.stage, ev.data.detail || '', ev.data.t + 'ms');
    }
    if (ev.data.type === 'bootError') {
      console.error('[SlateIframe] boot error', ev.data.error);
    }

    // Shared handlers
    if (ev.data.type === 'openFullScreenImage') {
      this.setState({fullScreenImageUuid: ev.data.message});
    }
    if (ev.data.type === 'setSlateImageUrl') {
      if (ev.data.message) {
        this.setState({imageUrl: ev.data.message});
      } else {
        this.setState({
          imageUrl: ev.data.message,
          colors: [],
        });
      }
    }
    if (ev.data.type === 'copyTextToClipboard') {
      var text = ev.data.message;
      var textField = document.createElement('textarea')
      textField.innerText = text

      // made table invisible
      textField.style.position = 'absolute';
      textField.style.top = '100%';
      textField.style.fontFamily = 'initial';
      textField.style.fontSize = 'initial';
      textField.style.backgroundColor = 'transparent';

      document.body.appendChild(textField)
      textField.select()
      document.execCommand('copy')
      textField.remove()
      this.props.showNotification(
        notificationTypes.SUCCESS,
        this.props.t('notifications:copyDataToClipboardSuccessfully'),
      );
    }
    if (ev.data.type === 'copyImageToClipboard') {
      await this.copyImg(ev.data.message);
    }
    if (ev.data.type === 'copyQRToClipboard') {
      await this.copyImg(api.GET_SLATE + '/' + ev.data.message + '/qr?size=1000');
    }
    if (ev.data.type === 'sendEmail') {
      window && window.open('mailto:' + ev.data.message, '_self')
    }
    if (ev.data.type === 'makeCall') {
      window && window.open('tel:' + ev.data.message, '_self')
    }
    if (ev.data.type === 'forceUpdatePage') {
      this.props.updatePage();
    }
    if (ev.data.type === iframeMessageTypes.PRINT_PDF && isPrintPdfMessage(ev.data)) {
      this.handlePrintPdf(ev.data);
    }

    // Protocol handlers -- forward to parent via callbacks
    if (ev.data.type === iframeMessageTypes.NAVIGATE_ACCEPTED) {
      this.loadedSlateUUID = ev.data.uuid;
      this.currentContentMode = ev.data.mode;
    }
    if (ev.data.type === iframeMessageTypes.NAVIGATE_REJECTED) {
      if (this.isContentPending()) {
        if (ev.data.reason === 'saveTimeout') {
          this.waitForSave();
        } else {
          this.reloadForContent('navigateRejected');
        }
      } else if (this.props.onNavigateRejected) {
        this.props.onNavigateRejected(this.loadedSlateUUID);
      }
    }
    if (ev.data.type === iframeMessageTypes.CONTENT_LOADED) {
      this.loadedSlateUUID = ev.data.uuid;
      this.clearContentTimers();
      if (this.props.onContentLoaded) {
        this.props.onContentLoaded(ev.data.uuid, this.currentContentMode);
      }
    }
    if (ev.data.type === iframeMessageTypes.CONTENT_CHANGED) {
      if (this.props.onContentChanged) {
        this.props.onContentChanged(ev.data.slateData);
      }
    }
    if (ev.data.type === iframeMessageTypes.CONTENT_SAVED) {
      if (this.saveResendTimer !== null) {
        this.clearSaveResend();
        this.postNavigate();
      }
      if (this.props.onContentSaved) {
        this.props.onContentSaved(this.currentContentMode);
      }
    }
    if (ev.data.type === iframeMessageTypes.CONTENT_ERROR) {
      console.error('contentError:', ev.data.error);
      if (this.isContentPending()) {
        this.reloadForContent('contentError');
      }
    }
    if (ev.data.type === iframeMessageTypes.RESET_ACCEPTED) {
      this.loadedSlateUUID = null;
      this.currentContentMode = null;
    }

    // Typed flutterReady (new protocol)
    if (ev.data.type === 'flutterReady') {
      this.handleFlutterReady();
    }
  }

  componentDidMount = () => {
    window.addEventListener('message', this.handler);
  };

  componentDidUpdate(prevProps) {
    // Before the flutterReady guard: a change during a reboot must still reset the reload count.
    var contentChanged = this.props.contentJson !== prevProps.contentJson
      && JSON.stringify(this.props.contentJson) !== JSON.stringify(prevProps.contentJson);
    if (contentChanged) {
      this.contentReloaded = false;
      this.clearSaveWait();
    }
    if (!this.props.contentJson) {
      this.clearContentTimers();
    }

    if (!this.flutterReady) return;

    if (this.props.slateUUID && this.props.slateUUID !== prevProps.slateUUID
      && this.props.slateUUID !== this.loadedSlateUUID) {
      this.sendNavigateMessage();
    }

    if (this.props.forceRefresh
      && this.props.forceRefresh !== prevProps.forceRefresh) {
      this.sendNavigateMessage();
    }

    if (this.props.contentJson && contentChanged) {
      this.sendNavigateMessage();
    }

    var prevUserPayload = JSON.stringify(this.buildUserPayload(prevProps.currentUser));
    var currUserPayload = JSON.stringify(this.buildUserPayload());
    if (prevUserPayload !== currUserPayload) {
      this.sendMessageToFlutter(iframeMessageTypes.UPDATE_USER, {
        user: this.buildUserPayload(),
      });
    }

    if (this.props.currentAccount
      && (prevProps.currentAccount !== this.props.currentAccount
        || prevProps.accountsList !== this.props.accountsList)) {
      this.sendMessageToFlutter(iframeMessageTypes.UPDATE_STACKS, {
        stacks: this.buildStacksPayload(),
        currentStackUuid: this.props.currentAccount.uuid,
      });
    }
  }

  componentWillUnmount() {
    this.clearContentTimers();
    this.releasePrint();
    window.removeEventListener('message', this.handler);
  }

  isCaptureOn = () => {
    return isPrintCaptureOn(process.env.NODE_ENV, function () { return window.localStorage; });
  }

  // Frame, URL and job of the current print. Safe to call when nothing is held.
  releasePrint = () => {
    var job = this.printJob;
    if (!job) return;
    this.printJob = null;
    if (job.frameJob) {
      job.frameJob.release();
      job.frameJob = null;
    }
    revokePdfUrl(job.url);
  }

  handlePrintPdf = (msg) => {
    this.releasePrint();
    this.setState({printDialog: null});
    var job = {url: createPdfUrl(msg.bytes), kind: msg.kind, filename: msg.filename, frameJob: null};
    this.printJob = job;
    var capture = this.isCaptureOn();
    var newTab = printsInNewTab(window.navigator.userAgent, window.navigator.maxTouchPoints);
    console.log('[SlateIframe] printPdf path=' + (newTab ? 'tab' : 'frame') + ' kind=' + msg.kind
      + ' bytes=' + msg.bytes.byteLength + ' capture=' + (capture ? 1 : 0));
    if (newTab) {
      this.setState({printDialog: {kind: msg.kind}});
      return;
    }
    if (capture) {
      downloadPdf(job.url, job.filename);
      return;
    }
    job.frameJob = printInFrame(job.url, {
      onPrinted: () => {
        if (this.printJob !== job) return;
        console.log('[SlateIframe] printPdf frame print called');
      },
      onFail: (reason) => {
        if (this.printJob !== job) return;
        this.handlePrintFailed(reason);
      },
      onReleased: () => {
        if (this.printJob !== job) return;
        this.releasePrint();
      },
    });
  }

  handlePrintFailed = (reason) => {
    var job = this.printJob;
    if (!job) return;
    console.log('[SlateIframe] printPdf failed reason=' + reason);
    if (job.frameJob) {
      job.frameJob.release();
      job.frameJob = null;
    }
    this.setState({printDialog: {kind: job.kind}});
  }

  handlePrintDialogPrint = () => {
    var job = this.printJob;
    if (!this.state.printDialog || !job) return;
    if (this.isCaptureOn()) {
      downloadPdf(job.url, job.filename);
      this.setState({printDialog: null});
      return;
    }
    if (!openPdfTab(job.url)) {
      console.log('[SlateIframe] printPdf failed reason=popup-blocked');
      return;
    }
    this.setState({printDialog: null});
  }

  handlePrintDialogCancel = () => {
    if (!this.state.printDialog || !this.printJob) return;
    this.setState({printDialog: null});
    this.releasePrint();
  }

  getLink = () => {
    var fullScreenImageUuid = this.state.fullScreenImageUuid;
    if (fullScreenImageUuid) {
      return fullScreenImageUuid.startsWith("base64:")
        ? "data:image/jpeg;base64," + fullScreenImageUuid.replace("base64:", '')
        : fullScreenImageUuid;
    }
    return '';
  };

  getColors = (colors) => {
    this.setState(
      (state) => ({colors: state.colors.concat(colors)}),
      () => {
        if (this.props.onColorChange) {
          this.props.onColorChange(this.state.colors);
        }
      }
    );
  }

  render() {
    var wrapClassName = this.props.wrapClassName || 'slate-full-page-wrap';
    var iframeClassName = this.props.iframeClassName || 'slate-full-page';
    var iframeInitSrc = process.env.REACT_APP_WEB_FLUTTER_URL;
    var printDialog = this.state.printDialog;
    var t = this.props.t;

    return (
      <React.Fragment>
        <div className={wrapClassName}>
          <div className="slate-pane-loader">
            <BeatLoader color="#f15a29" size={15} />
          </div>
          <div className="slate-pane-iframe-container">
            <iframe key={this.state.iframeKey}
                    ref={this.iframeRef}
                    title="Slate Details"
                    className={iframeClassName}
                    src={iframeInitSrc}
                    frameBorder="0"
                    allow="geolocation; accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    onLoad={function () { console.log('[SlateIframe] iframe load', iframeInitSrc); }}
            />
          </div>
        </div>
        <ZoomPanPinch
          imageSrc={this.state.fullScreenImageUuid ? this.getLink() : null}
          isOpen={this.state.fullScreenImageUuid}
          onClose={() => this.setState({fullScreenImageUuid: null})}
        />
        <ColorExtractor getColors={this.getColors}>
          <img
            src={this.state.imageUrl}
            alt=""
            style={{width: 0, height: 0,}}
          />
        </ColorExtractor>
        <SlateDialog
          open={!!printDialog}
          title={printDialog ? t(printDialog.kind === 'entry' ? 'slate:printEntry' : 'slate:printSlate') : ''}
          onClose={this.handlePrintDialogCancel}
          actions={printDialog ? [
            {key: 'cancel', label: t('btn:cancel'), variant: 'secondary', onClick: this.handlePrintDialogCancel},
            {key: 'print', label: t('btn:print'), variant: 'primary', onClick: this.handlePrintDialogPrint},
          ] : []}
        >
          {printDialog && <p className="slate-print-dialog-text">{t('slate:printOpensInNewTab')}</p>}
        </SlateDialog>
      </React.Fragment>
    );
  }

  createImage = (options) => {
    options = options || {};
    const img = (Image) ? new Image() : document.createElement("img");
    if (options.src) {
      img.src = options.src;
    }
    return img;
  }

  convertToPng = (imgBlob) => {
    const imageUrl = window.URL.createObjectURL(imgBlob);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const imageEl = this.createImage({src: imageUrl});
    imageEl.onload = (e) => {
      canvas.width = e.target.width;
      canvas.height = e.target.height;
      ctx.drawImage(e.target, 0, 0, e.target.width, e.target.height);
      canvas.toBlob(this.copyToClipboard, "image/png", 1);
    };
  }

  copyImg = async (src) => {
    const img = await fetch(src);
    const imgBlob = await img.blob();
    if (imgBlob.type.endsWith("jpg") || imgBlob.type.endsWith("jpeg")) {
      this.convertToPng(imgBlob);
    } else if (src.endsWith(".png")) {
      await this.copyToClipboard(imgBlob);
    } else {
      console.error("Format unsupported");
    }
  }

  copyToClipboard = async (pngBlob) => {
    try {
      await window.navigator.clipboard.write([
        new window.ClipboardItem({
          [pngBlob.type]: pngBlob
        })
      ]);

      this.props.showNotification(
        notificationTypes.SUCCESS,
        this.props.t('notifications:copyDataToClipboardSuccessfully'),
      );
    } catch (error) {
      this.props.showNotification(
        notificationTypes.ERROR,
        this.props.t('notifications:copyDataToClipboardFailed'),
      );
      console.error(error);
    }
  }
}

var mapStateToProps = function (state) {
  return {
    currentAccount: state.accounts.currentAccount,
    accountsList: state.accounts.accountsList,
    currentUser: state.currentUser,
  };
};

var mapDispatchToProps = {
  showNotification: showNotification,
  updatePage: updatePage,
};

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(SlateIframe),
);
